import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useTrainingPrograms } from "../hooks";
import type { TrainingProgramStatus } from "../types";

const STATUS_STYLES: Record<TrainingProgramStatus, string> = {
  DRAFT:     "bg-slate-100 text-slate-600",
  PUBLISHED: "bg-emerald-100 text-emerald-700",
  CANCELLED: "bg-red-100 text-red-600",
  COMPLETED: "bg-indigo-100 text-indigo-700",
};

function fmtDate(v?: string | null) {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

export default function TrainingProgramsPage() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const query = useTrainingPrograms(page, 15, status || undefined, search || undefined);

  if (query.isLoading) return <PageLoader label="Loading programs…" />;
  if (query.isError) return <ErrorState title="Failed to load" message={String(query.error)} />;

  const data = query.data!;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Training Programs</h1>
        </div>
        <Link
          to="/hr/training/programs/new"
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" /> New Program
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Search programs…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        />
        <select className="input w-auto min-w-[160px]" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <option value="">All statuses</option>
          {(["DRAFT","PUBLISHED","COMPLETED","CANCELLED"] as TrainingProgramStatus[]).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {data.content.length === 0 ? (
        <EmptyState title="No programs found" description="Create a new program and publish it to enrol employees." />
      ) : (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden divide-y">
            {data.content.map((p) => (
              <Link
                key={p.id}
                to={`/hr/training/programs/${p.id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{p.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {p.categoryName && `${p.categoryName} · `}
                    {p.providerName && `${p.providerName} · `}
                    {fmtDate(p.startDate) ?? "No date"}
                    {p.enrollmentCount != null && ` · ${p.enrollmentCount} enrolled`}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[p.status]}`}>
                  {p.status}
                </span>
              </Link>
            ))}
          </div>
          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
